import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { loadMe } from './me';

/**
 * 화면 조건 기억(2026-09-29 게시판 #37 2번) — 「메뉴를 옮겼다 돌아와도 보던 조건 그대로」 · 「로그인하면 용인으로 시작」.
 *
 * `useState` 와 같은 모양이다. 두 층:
 * - **화면 메모리**(모든 키) — 메뉴를 오가도 남고, 새로고침·로그아웃하면 사라진다. 기간·검색어·상태 필터는 여기까지만 —
 *   날짜를 다음 날까지 들고 가면 어제 목록이 뜬다.
 * - **계정 저장**(`persist: true` 키만) — 서버 `user_pref`(getMe 가 실어 온다). 다른 PC 에서도 같다.
 *   브라우저에 두지 않는 이유: 개선요청 읽음을 브라우저에 뒀다가 「다른 PC 에 오니 다르다」(improveSeen.ts).
 *
 * 🔴 계정 값은 getMe 가 온 뒤에 들어온다 — 그 사이 사람이 이미 바꿨으면 덮지 않는다(고른 걸 되돌리면 안 된다).
 * 🔴 저장 실패는 조용히 넘긴다 — 기억 못 해도 화면은 돌아야 한다.
 */
const memory = new Map<string, unknown>();

/** 로그아웃 때 비운다 — 다음 사람이 앞사람 조건을 물고 시작하지 않게(`clearMe` 와 같이 부른다). */
export function clearSticky(): void {
  memory.clear();
}

export function useSticky<T>(
  key: string,
  init: T | (() => T),
  opts: { persist?: boolean; valid?: (v: unknown) => v is T } = {},
): [T, (v: T | ((prev: T) => T)) => void] {
  const { persist = false, valid } = opts;
  const [v, setV] = useState<T>(() =>
    memory.has(key) ? (memory.get(key) as T) : typeof init === 'function' ? (init as () => T)() : init);
  const touched = useRef(memory.has(key));

  useEffect(() => {
    if (!persist || touched.current) return;
    let cancelled = false;
    loadMe().then((me) => {
      const saved = me.prefs?.[key];
      if (cancelled || touched.current || saved === undefined || (valid && !valid(saved))) return;
      memory.set(key, saved);
      setV(saved as T);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [key, persist, valid]);

  const cur = useRef(v);
  cur.current = v;
  const set = useCallback((next: T | ((prev: T) => T)) => {
    touched.current = true;
    const prev = cur.current;
    const val = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
    cur.current = val;
    memory.set(key, val);
    setV(val);
    if (persist && val !== prev) {
      api.setMyPref({ key, value: val ?? null }).catch(() => undefined);
      // 같은 로그인 안에서(getMe 를 다시 받기 전) 새로 여는 화면도 새 값을 보게.
      loadMe().then((me) => { me.prefs = { ...(me.prefs ?? {}), [key]: val }; }).catch(() => undefined);
    }
  }, [key, persist]);

  return [v, set];
}

/** 회계 목록이 같이 쓰는 법인 필터 — 한 화면에서 고르면 다른 회계 화면도 그 법인으로 연다(정산년월 기억과 같은 이유 — 저장 위치 하나). */
export const isCorpId = (x: unknown): x is number | null => x === null || (typeof x === 'number' && Number.isInteger(x) && x > 0);
export const useAcctCorp = () => useSticky<number | null>('acct.corp', null, { persist: true, valid: isCorpId });

/** 훅 밖에서 화면 메모리 값만 슬쩍 본다(없으면 undefined) — 화면 탭 키를 정할 때(#211). */
export const peekSticky = (key: string): unknown => memory.get(key);
